import React, { useState, useEffect, useMemo } from 'react';
import './TiendaPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';
import StoreCarousel from './StoreCarousel';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { STORE_CATEGORY_SLUGS } from '../constants/storeCategories';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/** Igual que en MySQL tras unify_product_categories.sql: minúsculas y guiones. */
const normalizeCategorySlug = (raw) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const VALID_CATEGORIES = STORE_CATEGORY_SLUGS;

/** Etiquetas en UI (breadcrumb / título); los slugs siguen siendo los de URL/BD. */
const CATEGORY_UI_LABELS = {
  ropa: 'Ropa',
  'ropa-hombre': 'Ropa hombre',
  'ropa-mujer': 'Ropa mujer',
  'productos-naturales': 'Productos naturales',
  'instrumentos-musicales': 'Instrumentos musicales',
  'joyeria-artesanias': 'Joyería y Artesanías',
  bienestar: 'Bienestar',
  tecnologia: 'Tecnología',
  jardineria: 'Jardinería',
  sale: 'Ofertas (descuentos)'
};

const getCategoryFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const cat = normalizeCategorySlug(params.get('category'));
  if (VALID_CATEGORIES.includes(cat)) return cat;
  return 'ropa';
};

const TiendaPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(getCategoryFromUrl);
  const [apiProducts, setApiProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('latest');
  /** Escritorio: panel categorías abierto. Móvil: cerrado hasta pulsar "Filtros". */
  const [filtersOpen, setFiltersOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 993px)').matches : true
  );

  const { addToCart, openCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Si cambia querystring en /tienda, mantener el filtro sincronizado sin recargar.
  useEffect(() => {
    const syncCategoryFromUrl = () => setSelectedCategory(getCategoryFromUrl());
    window.addEventListener('popstate', syncCategoryFromUrl);
    return () => window.removeEventListener('popstate', syncCategoryFromUrl);
  }, []);

  // Sincronizar URL con categoría seleccionada (/tienda limpio = vista "Ropa" completa)
  useEffect(() => {
    const base = window.location.pathname;
    if (selectedCategory === 'ropa') {
      window.history.replaceState({}, '', base);
    } else {
      window.history.replaceState({}, '', `${base}?category=${selectedCategory}`);
    }
  }, [selectedCategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/products`, { params: { limit: 100 } });
        const productsData = response.data.data?.products || response.data.products || [];
        setApiProducts(Array.isArray(productsData) ? productsData : []);
        setError(null);
      } catch (err) {
        console.error('Error cargando productos:', err);
        setError('Error al cargar los productos. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Carrusel - imágenes IA de fondo
  const carouselSlides = [
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-coleccion-javiland.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/miel.png`,
      subtitle: 'Ropa',
      title: 'Camiseta Javiland',
      description: 'Diseño exclusivo. Hecho en Colombia.',
      cta: { text: 'Ver', onClick: () => setSelectedCategory('ropa') }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-cafe-kaaba.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/miel.png`,
      subtitle: 'Productos naturales',
      title: 'Shampoo, jabón y más',
      description: 'Productos naturales para el cuidado personal.',
      cta: { text: 'Ver', onClick: () => setSelectedCategory('productos-naturales') }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-instrumentos-musicales.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/miel.png`,
      subtitle: 'Instrumentos musicales',
      title: 'Ukelele, campanas, cuencos',
      description: 'Sonidos que sanan y conectan.',
      cta: { text: 'Ver', onClick: () => setSelectedCategory('instrumentos-musicales') }
    }
  ];

  const mapApiToCard = (p) => {
    const price = parseFloat(p.price ?? 0);
    const rawOriginal =
      parseFloat(p.originalPrice ?? p.original_price ?? p.compare_at_price ?? p.compareAtPrice ?? price);
    const originalPrice = Number.isFinite(rawOriginal) && rawOriginal > price ? rawOriginal : price;
    const stock = parseInt(p.stock ?? 0, 10);
    const img = (p.images && p.images[0]) || p.image || '/images/placeholders/placeholder-product.png';
    return {
      id: p.id,
      brand: p.brand || 'Javiland',
      name: p.name,
      category: p.category,
      price,
      originalPrice,
      image: img,
      images: p.images || [img],
      description: p.description || '',
      features: p.features || [],
      inStock: stock > 0 && (p.status === 'active' || p.status === undefined),
      stock,
      variants: [{ size: 'Único', color: 'Único', stock, price }],
      isCamisetaAgrupada: false,
      isNew: false
    };
  };

  /** Productos por categoría asignable en BD + vista combinada `ropa`. */
  const filteredProducts = useMemo(() => {
    const activeProducts = (apiProducts || []).filter(
      (p) => p.status !== 'inactive' && p.status !== 'upcoming'
    );

    const mappedForSale = activeProducts.map((p) => mapApiToCard(p));

    if (selectedCategory === 'sale') {
      return mappedForSale.filter((p) => p.originalPrice > p.price);
    }

    const buildListForAssignableSlug = (slug) => {
      const list = [];
      const byCat = activeProducts.filter((p) => normalizeCategorySlug(p.category) === slug);

      if (slug === 'ropa-hombre') {
        const camisetas = (apiProducts || []).filter((p) => p.name?.toLowerCase().includes('camiseta'));
        if (camisetas.length > 0) {
          const primeraCamiseta = camisetas[0];
          const productImage =
            primeraCamiseta.images?.length > 0
              ? primeraCamiseta.images[0]
              : primeraCamiseta.image || '/images/placeholders/placeholder-product.png';
          const totalStock = camisetas.reduce((sum, c) => sum + (c.stock || 0), 0);
          list.push({
            id: primeraCamiseta.id,
            brand: 'Javiland',
            name: 'Camiseta Javiland',
            category: 'ropa-hombre',
            price: parseFloat(primeraCamiseta.price),
            originalPrice: parseFloat(primeraCamiseta.price) * 1.2,
            image: productImage,
            images:
              primeraCamiseta.images || [
                primeraCamiseta.image || '/images/placeholders/placeholder-product.png'
              ],
            description:
              primeraCamiseta.description ||
              'Camiseta 100% algodón orgánico con diseño exclusivo de Javiland.',
            features: primeraCamiseta.features || [],
            inStock: totalStock > 0,
            stock: totalStock,
            variants: [
              {
                size: 'Seleccionar',
                color: 'Único',
                stock: totalStock,
                price: parseFloat(primeraCamiseta.price)
              }
            ],
            isCamisetaAgrupada: true,
            isNew: true
          });
        }
        const otrosRh = byCat.filter((p) => !p.name?.toLowerCase().includes('camiseta'));
        otrosRh.forEach((p) => list.push(mapApiToCard(p)));
      } else {
        byCat.forEach((p) => list.push(mapApiToCard(p)));
      }

      return list;
    };

    if (selectedCategory === 'ropa') {
      return [...buildListForAssignableSlug('ropa-hombre'), ...buildListForAssignableSlug('ropa-mujer')];
    }

    return buildListForAssignableSlug(selectedCategory);
  }, [apiProducts, selectedCategory]);

  const sidebarBlocks = useMemo(() => {
    const countInCategory = (catId) =>
      (apiProducts || []).filter(
        (p) =>
          normalizeCategorySlug(p.category) === catId &&
          p.status !== 'inactive' &&
          p.status !== 'upcoming'
      ).length;

    const ropaTotal = countInCategory('ropa-hombre') + countInCategory('ropa-mujer');

    const ropaItems = [
      { id: 'ropa', name: 'Ropa', count: ropaTotal, icon: 'fas fa-tshirt', nested: false },
      {
        id: 'ropa-hombre',
        name: 'Ropa hombre',
        count: countInCategory('ropa-hombre'),
        icon: 'fas fa-mars',
        nested: true
      },
      {
        id: 'ropa-mujer',
        name: 'Ropa mujer',
        count: countInCategory('ropa-mujer'),
        icon: 'fas fa-venus',
        nested: true
      }
    ];

    const otherItems = [
      {
        id: 'productos-naturales',
        name: 'Productos naturales',
        count: countInCategory('productos-naturales'),
        icon: 'fas fa-leaf',
        nested: false
      },
      {
        id: 'instrumentos-musicales',
        name: 'Instrumentos musicales',
        count: countInCategory('instrumentos-musicales'),
        icon: 'fas fa-music',
        nested: false
      },
      {
        id: 'joyeria-artesanias',
        name: 'Joyería y Artesanías',
        count: countInCategory('joyeria-artesanias'),
        icon: 'fas fa-gem',
        nested: false
      },
      {
        id: 'bienestar',
        name: 'Bienestar',
        count: countInCategory('bienestar'),
        icon: 'fas fa-spa',
        nested: false
      },
      {
        id: 'tecnologia',
        name: 'Tecnología',
        count: countInCategory('tecnologia'),
        icon: 'fas fa-laptop',
        nested: false
      },
      {
        id: 'jardineria',
        name: 'Jardinería',
        count: countInCategory('jardineria'),
        icon: 'fas fa-seedling',
        nested: false
      }
    ];

    const saleCount = (apiProducts || [])
      .filter((p) => p.status !== 'inactive' && p.status !== 'upcoming')
      .map((p) => mapApiToCard(p))
      .filter((p) => p.originalPrice > p.price).length;

    return { ropaItems, otherItems, saleCount };
  }, [apiProducts]);

  const selectedCategoryLabel =
    CATEGORY_UI_LABELS[selectedCategory] || selectedCategory;

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'latest') return 0;
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  const handleAddToCart = (product) => {
    try {
      const productForCart = {
        id: product.id,
        name: product.name,
        price: product.price,
        images: [product.image || '/images/placeholders/placeholder-product.png'],
        variants: [{ size: 'Único', color: 'Único', stock: product.stock }]
      };
      addToCart(productForCart, 'Único', 'Único', 1);
      openCart();
    } catch (err) {
      console.error('Error al agregar al carrito:', err);
      alert('Error al agregar el producto al carrito.');
    }
  };

  const goToProduct = (product) => {
    window.history.pushState({}, '', `/producto/${product.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="tienda-page">
      <Header />
      <AnimatedBackground particleCount={20} staticCount={6} />

      {/* Carrusel igual que Bienestar - imágenes IA de fondo */}
      <StoreCarousel slides={carouselSlides} autoplayInterval={6000} height="550px" variant="tienda" />

      <main className="tienda-main">
        <div className="tienda-layout">
          {/* Sidebar filtros - estilo Yucca */}
          <aside className={`tienda-sidebar ${filtersOpen ? 'open' : ''}`}>
            <button
              className="sidebar-toggle"
              onClick={() => setFiltersOpen(!filtersOpen)}
              aria-label={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
            >
              <i className="fas fa-filter"></i>
              {filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>

            <div className="sidebar-content">
              <h3 className="sidebar-title">Categorías</h3>
              <ul className="category-list">
                {sidebarBlocks.ropaItems.map((cat) => (
                  <li key={cat.id}>
                    <button
                      type="button"
                      className={`category-filter-btn ${cat.nested ? 'category-filter-btn--nested' : ''} ${selectedCategory === cat.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span className="category-check">
                        {selectedCategory === cat.id && <i className="fas fa-check"></i>}
                      </span>
                      <span className="category-name">{cat.name}</span>
                      <span className="category-count">({cat.count})</span>
                    </button>
                  </li>
                ))}
                {sidebarBlocks.otherItems.map((cat) => (
                  <li key={cat.id}>
                    <button
                      type="button"
                      className={`category-filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span className="category-check">
                        {selectedCategory === cat.id && <i className="fas fa-check"></i>}
                      </span>
                      <span className="category-name">{cat.name}</span>
                      <span className="category-count">({cat.count})</span>
                    </button>
                  </li>
                ))}
                <li className="category-subsection-label" aria-hidden="true">
                  Vistas especiales
                </li>
                <li>
                  <button
                    type="button"
                    className={`category-filter-btn ${selectedCategory === 'sale' ? 'active' : ''}`}
                    onClick={() => setSelectedCategory('sale')}
                    title="Sub-vista: todos los productos con descuento, sin importar su categoría. No se asigna al crear un producto."
                  >
                    <span className="category-check">
                      {selectedCategory === 'sale' && <i className="fas fa-check"></i>}
                    </span>
                    <span className="category-name">Ofertas (descuentos)</span>
                    <span className="category-count">({sidebarBlocks.saleCount})</span>
                  </button>
                </li>
              </ul>

              {/* Card info - estilo Yucca integrado */}
              <div className="sidebar-info-card">
                <div className="info-card-icon">
                  <i className="fas fa-truck"></i>
                </div>
                <p className="info-card-title">Envíos a Colombia</p>
                <p className="info-card-desc">Entrega a todo el país. Consulta tarifas al finalizar tu compra.</p>
              </div>

              <div className="sidebar-info-card">
                <div className="info-card-icon">
                  <i className="fas fa-hand-holding-heart"></i>
                </div>
                <p className="info-card-title">Hecho con amor</p>
                <p className="info-card-desc">Productos auténticos desde Colombia para el mundo.</p>
              </div>
            </div>
          </aside>

          {/* Área principal productos */}
          <div className="tienda-content">
            <header className="tienda-header">
              <div className="breadcrumb">
                <span>Home</span>
                <i className="fas fa-chevron-right"></i>
                <span>Tienda</span>
                {selectedCategory && (
                  <>
                    <i className="fas fa-chevron-right"></i>
                    <span>{selectedCategoryLabel}</span>
                  </>
                )}
              </div>

              <h1 className="tienda-page-title">{selectedCategoryLabel}</h1>

              <div className="tienda-toolbar">
                <span className="product-count">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'producto' : 'productos'}
                </span>

                <div className="toolbar-actions">
                  <div className="sort-dropdown">
                    <label htmlFor="sort-select">Ordenar por:</label>
                    <select
                      id="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select"
                    >
                      <option value="latest">Más recientes</option>
                      <option value="price-asc">Precio: menor a mayor</option>
                      <option value="price-desc">Precio: mayor a menor</option>
                      <option value="name">Nombre A-Z</option>
                    </select>
                  </div>
                </div>
              </div>
            </header>

            {loading && (
              <div className="tienda-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Cargando productos...</p>
              </div>
            )}

            {error && (
              <div className="tienda-error">
                <i className="fas fa-exclamation-circle"></i>
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && filteredProducts.length === 0 && (
              <div className="tienda-empty">
                <i className={`fas ${
                  ['ropa', 'ropa-hombre', 'ropa-mujer'].includes(selectedCategory)
                    ? 'fa-tshirt'
                    : selectedCategory === 'instrumentos-musicales'
                    ? 'fa-music'
                    : selectedCategory === 'joyeria-artesanias'
                    ? 'fa-gem'
                    : selectedCategory === 'bienestar'
                    ? 'fa-spa'
                    : selectedCategory === 'tecnologia'
                    ? 'fa-laptop'
                    : selectedCategory === 'jardineria'
                    ? 'fa-seedling'
                    : selectedCategory === 'sale'
                    ? 'fa-tags'
                    : 'fa-leaf'
                }`}></i>
                <p>
                  {selectedCategory === 'ropa'
                    ? 'No hay productos de ropa por ahora.'
                    : selectedCategory === 'ropa-mujer'
                    ? 'Ropa mujer próximamente.'
                    : selectedCategory === 'productos-naturales'
                    ? 'Productos naturales próximamente. Shampoo, jabón y más.'
                    : selectedCategory === 'instrumentos-musicales'
                    ? 'Instrumentos musicales próximamente. Ukelele, campana salsera, cuenco tibetano.'
                    : selectedCategory === 'joyeria-artesanias'
                    ? 'Joyería y artesanías próximamente.'
                    : selectedCategory === 'bienestar'
                    ? 'Bienestar próximamente. Mat de yoga y más.'
                    : selectedCategory === 'tecnologia'
                    ? 'Tecnología próximamente.'
                    : selectedCategory === 'jardineria'
                    ? 'Jardinería próximamente.'
                    : selectedCategory === 'sale'
                    ? 'No hay productos en SALE por ahora.'
                    : 'No hay productos en esta categoría.'}
                </p>
              </div>
            )}

            {!loading && !error && filteredProducts.length > 0 && (
              <div className="products-grid">
                {/* Productos */}
                {sortedProducts.map(product => (
                  <article
                    key={product.id}
                    className="product-card"
                    onClick={(e) => {
                      if (e.target.closest('.product-quick-actions')) return;
                      goToProduct(product);
                    }}
                  >
                    <div className="product-image-wrap">
                      <img
                        src={product.image || product.images?.[0] || '/images/placeholders/placeholder.svg'}
                        alt={product.name}
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholders/placeholder.svg';
                        }}
                      />
                      {product.isNew && <span className="product-badge new">Nuevo</span>}
                      {!product.inStock && <span className="product-badge soldout">Agotado</span>}
                      {product.originalPrice > product.price && (
                        <span className="product-badge discount">
                          -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                        </span>
                      )}

                      <div className="product-quick-actions">
                        <button
                          className="quick-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (product.isCamisetaAgrupada) goToProduct(product);
                            else if (product.inStock) handleAddToCart(product);
                          }}
                          disabled={!product.inStock && !product.isCamisetaAgrupada}
                          title={product.isCamisetaAgrupada ? 'Seleccionar talla' : 'Agregar al carrito'}
                        >
                          <i className={product.isCamisetaAgrupada ? 'fas fa-ruler-combined' : 'fas fa-shopping-bag'}></i>
                        </button>
                        <button
                          className="quick-action-btn"
                          onClick={(e) => { e.stopPropagation(); goToProduct(product); }}
                          title="Ver producto"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </div>

                    <div className="product-info">
                      {product.brand && (
                        <span className="product-brand">{product.brand}</span>
                      )}
                      <h3 className="product-name">{product.name}</h3>
                      <div className="product-pricing">
                        <span className="current-price">${product.price.toLocaleString()}</span>
                        {product.originalPrice > product.price && (
                          <>
                            <span className="product-discount-badge">
                              -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                            </span>
                            <span className="original-price">${product.originalPrice.toLocaleString()}</span>
                          </>
                        )}
                      </div>
                      <button
                        className="product-cta"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.isCamisetaAgrupada) goToProduct(product);
                          else handleAddToCart(product);
                        }}
                        disabled={!product.inStock && !product.isCamisetaAgrupada}
                      >
                        {product.isCamisetaAgrupada ? 'Seleccionar talla' : (product.inStock ? 'Agregar' : 'Agotado')}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TiendaPage;
